import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Button } from '@twilio-paste/button';
import { Stack } from '@twilio-paste/stack';
import Changelog from '@twilio-paste/button/CHANGELOG.md';
import { PlusIcon } from '@twilio-paste/icons/esm/PlusIcon';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { SidebarCategoryRoutes } from '../../../constants';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/button"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/button/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name="Button" categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/button" storybookUrl="/?path=/story/components-button--primary-button" abstractUrl="https://share.goabstract.com/58f1e53e-9f3e-4a04-b84e-44918d1ccdaf" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <h3>{`About buttons`}</h3>
        <p>{`A button communicates that users can trigger an action. Places you’d use
a button include:`}</p>
        <ul>
          <li parentName="ul">{`Submitting a form`}</li>
          <li parentName="ul">{`Closing a modal`}</li>
          <li parentName="ul">{`Moving to the next step in a flow`}</li>
        </ul>
        <p>{`A button can contain an icon and/or text. See Composing a button below
for more detailed guidelines.`}</p>
        <h4>{`Button vs. Anchor (Link)`}</h4>
        <p><strong parentName="p">{`TL;DR`}</strong>{` If pressing the trigger results in a URL change, or that resultant
page makes sense as a whole new browser tab use an `}<strong parentName="p">{`Anchor`}</strong>{` element.
Everything else is a `}<strong parentName="p">{`Button`}</strong>{`.`}</p>
        <p>{`It may not be immediately obvious but the semantic distinction between an
anchor and button HTML element is extremely important to learn. Without
realising it the decision can cause significant usability problems due to
the in-built behaviours, interactions and expectations that come with each.`}</p>
        <p>{`For example, an anchor element will perform its action when clicked and
when the enter key is pressed. A button element will perform its action
when clicked and when the enter and spacebar keys are pressed. When
holding the control or command key an anchor will open a new browser
tab, a button will not.`}</p>
        <p>{`You should:`}</p>
        <ul>
          <li parentName="ul">{`Use an `}<strong parentName="li">{`Anchor`}</strong>{` when you are `}<strong parentName="li">{`navigating the user`}</strong>{` to a new page or
place on the page`}</li>
          <li parentName="ul">{`Use a `}<strong parentName="li">{`Button`}</strong>{` when the user is `}<strong parentName="li">{`performing an action`}</strong>{`. An action always
happens on the same page as the trigger`}</li>
        </ul>
        <p>{`If you need to link to content, you can use our `}<a parentName="p" {...{
            "href": "/components/anchor"
          }}>{`Anchor component`}</a>{`.`}</p>
        <h4>{`Accessibility`}</h4>
        <p>{`For accessibility, the distinction becomes even more important,
especially for those who are using Assistive Technology (A.T.) such as
screen readers and dictation software. Here are some quick tips:`}</p>
        <ul>
          <li parentName="ul">{`Correctly choosing between an anchor or button element will help inform A.T.
users what will happen next. Will I be taken to an entirely new page or will
something happen on the current page?`}</li>
          <li parentName="ul">{`Choose button text that clearly describes the action that is about to happen`}</li>
          <li parentName="ul">{`Don’t repeat the same button text on the same page. Try not to have 20
“edit” buttons, add clarifying text, even if it’s visually hidden, to
fully describe the action. E.g. “edit home address”, “add new phone number”`}</li>
          <li parentName="ul">{`Don’t communicate with color alone. When choosing a destructive button,
make sure the button text also indicates the action is destructive`}</li>
          <li parentName="ul">{`Use anchors that look like buttons sparingly. Voice dictation users may encounter issues when trying to activate them. A user may say, “Click the read more button”, but the dictation software won’t respond since it can’t tell what the anchor looks like visually. Use an alternative where possible, such as an anchor with an icon or with a larger font size.`}</li>
        </ul>
        <h3>{`Examples`}</h3>
        <LivePreview scope={{
          Button,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="primary">
    ✊ Action
  </Button>
  <Button variant="secondary">Action</Button>
  <Button variant="destructive">Action</Button>
  <Button variant="destructive_secondary">Action</Button>
  <Button variant="primary" size="small">
    Action
  </Button>
  <Button variant="secondary" size="small">
    Action
  </Button>
  <Button variant="destructive" size="small">
    Action
  </Button>
  <Button variant="destructive_secondary" size="small">
    Action
  </Button>
  <Button variant="link">Action</Button>
  <Button variant="destructive_link">Action</Button>
  <Button variant="reset" size="reset">
    Action
  </Button>
</Stack>`}
        </LivePreview>
        <h4>{`Primary button`}</h4>
        <p>{`Use a primary button to indicate the most prominent action a customer would
make on a screen. It should be a safe and if possible, reversible action
without much cost.`}</p>
        <p>{`Try to use only one primary button on a screen. Using multiple might be
distracting.`}</p>
        <LivePreview scope={{
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Button variant="primary">
  Save
</Button>`}
        </LivePreview>
        <h4>{`Secondary button`}</h4>
        <p>{`The secondary button is the most frequently used button.`}</p>
        <p>{`Use a secondary button to indicate an action that should be easy for a customer
to make but isn’t the most prominent on a screen. It should be a safe and if
possible, reversible action without much cost.`}</p>
        <LivePreview scope={{
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Button variant="secondary">
  Cancel
</Button>`}
        </LivePreview>
        <h4>{`Destructive secondary button`}</h4>
        <p>{`A destructive secondary button indicates a destructive action, such as “Delete” or
“Remove”, that might be difficult to reverse. If possible, give users
the ability to undo the action, or at least, confirm the action.`}</p>
        <LivePreview scope={{
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Button variant="destructive_secondary">
  Delete
</Button>`}
        </LivePreview>
        <h4>{`Destructive button`}</h4>
        <p>{`Use a destructive primary button for the most prominent action a customer would make on a
screen if that action is destructive and could be difficult to reverse.`}</p>
        <LivePreview scope={{
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Button variant="destructive">
  Delete
</Button>`}
        </LivePreview>
        <h4>{`Icon-only button`}</h4>
        <p>{`Use icon-only buttons sparingly.`}</p>
        <p>{`They should be used only in compact UI situations. Use an icon that can
only convey a single action.`}</p>
        <LivePreview scope={{
          Button,
          Stack,
          PlusIcon
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="primary" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="secondary" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="link" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive_secondary" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive_link" size="icon">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="primary" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="secondary" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="link" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive_secondary" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
  <Button variant="destructive_link" size="icon_small">
    <PlusIcon decorative={false} title="Add to cart" />
  </Button>
</Stack>`}
        </LivePreview>
        <h4>{`Link-style button`}</h4>
        <p>{`Use link-style buttons when other types of buttons may be too distracting.`}</p>
        <Callout mdxType="Callout">
  <CalloutTitle as="h4" mdxType="CalloutTitle">Hot accessibility tip</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    To reiterate, be mindful when choosing this variant as dictation software users may experience usability issues.
    Read the guidelines first.
  </CalloutText>
        </Callout>
        <LivePreview scope={{
          Button,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="link">Edit</Button>
  <Button variant="destructive_link">
    Delete
  </Button>
</Stack>`}
        </LivePreview>
        <h4>{`Small button`}</h4>
        <p>{`Use small buttons sparingly, only when needed for vertical density.
Guidelines for using variants in small buttons are the same as in
their default size.`}</p>
        <LivePreview scope={{
          Button,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="primary" size="small">
    Save
  </Button>
  <Button variant="secondary" size="small">
    Cancel
  </Button>
  <Button variant="destructive" size="small">
    Delete
  </Button>
  <Button variant="destructive_secondary" size="small">
    Delete
  </Button>
</Stack>`}
        </LivePreview>
        <h4>{`Reset button styles`}</h4>
        <p>{`The reset button is basically a primitive button. The button styles are
reset, so styles won't interfere with any child elements. This allows you to
create your own buttons using the Paste. This reset variant only resets
styles. No other button functionality is reset.`}</p>
        <LivePreview scope={{
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Button variant="reset" size="reset">
  Edit
</Button>`}
        </LivePreview>
        <h3>{`States`}</h3>
        <h4>{`Loading`}</h4>
        <p>{`Use the loading state if the action doesn’t happen instantly.
The button is also disabled in this state.`}</p>
        <p>{`However, the loading state may make an action appear to take longer
than it does and doesn’t communicate what’s preventing the action
from completing. Use it when you can’t move the user to the next state.`}</p>
        <LivePreview scope={{
          Button,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="primary" loading>
    Submit
  </Button>
  <Button variant="secondary" loading>
    Submit
  </Button>
  <Button variant="destructive" loading>
    Submit
  </Button>
  <Button variant="destructive_secondary" loading>
    Submit
  </Button>
  <Button variant="primary" size="small" loading>
    Submit
  </Button>
  <Button variant="secondary" size="small" loading>
    Submit
  </Button>
  <Button variant="destructive" size="small" loading>
    Submit
  </Button>
  <Button variant="destructive_secondary" size="small" loading>
    Submit
  </Button>
  <Button variant="link" loading>
    Submit
  </Button>
  <Button variant="destructive_link" loading>
    Submit
  </Button>
  <Button variant="reset" size="reset" loading>
    Submit
  </Button>
</Stack>`}
        </LivePreview>
        <h4>{`Disabled`}</h4>
        <p>{`Use the disabled state sparingly.`}</p>
        <p>{`The customer shouldn't have to guess why a button is disabled.
It should be immediately obvious to the customer why a button
might be disabled (`}<em parentName="p">{`e.g.`}</em>{`, if it follows a single empty text
field). Otherwise, show the button in its default state, then
provide helpful error text after it's pressed.`}</p>
        <LivePreview scope={{
          Button,
          Stack
        }} language="jsx" mdxType="LivePreview">
  {`<Stack orientation="horizontal" spacing="space30">
  <Button variant="primary" disabled>
    Action
  </Button>
  <Button variant="secondary" disabled>
    Action
  </Button>
  <Button variant="destructive" disabled>
    Action
  </Button>
  <Button variant="destructive_secondary" disabled>
    Action
  </Button>
  <Button variant="primary" size="small" disabled>
    Action
  </Button>
  <Button variant="secondary" size="small" disabled>
    Action
  </Button>
  <Button variant="destructive" size="small" disabled>
    Action
  </Button>
  <Button variant="destructive_secondary" size="small" disabled>
    Action
  </Button>
  <Button variant="link" disabled>
    Action
  </Button>
  <Button variant="destructive_link" disabled>
    Action
  </Button>
  <Button variant="reset" size="reset" disabled>
    Action
  </Button>
</Stack>`}
        </LivePreview>
        <h3>{`Composing a button`}</h3>
        <p>{`In most cases, you’ll use a text-only button.`}</p>
        <p>{`Button text should:`}</p>
        <ul>
          <li parentName="ul">{`Use sentence case (“Log in”, not “Log In”)`}</li>
          <li parentName="ul">{`Clearly indicate what’ll happen when a user presses it.`}</li>
          <li parentName="ul">{`Start with a verb, except for a common action like “Done.”`}</li>
          <li parentName="ul">{`Be concise without sacrificing clarity and user confidence.`}</li>
        </ul>
        <p>{`Pair text with an icon only if the icon clarifies the meaning of
the button. Use no more than one icon before text and one icon after text.`}</p>
        <h3>{`When to use a button`}</h3>
        <p>{`Use a button to indicate that users can trigger an action.`}</p>
        <p>{`In general, align buttons to the direction of the text (`}<em parentName="p">{`e.g.`}</em>{`,
left-aligned in English) for easy scannability.`}</p>
        <p>{`When moving customers through a sequence, place the primary button in
the direction of the movement (e.g., a “Next” button goes on the
right in an English-language flow).`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Prioritize actions on a screen. Only one primary button should be used on each screen so users are clear about what the intended action is." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don’t use many primary buttons on a screen, which may distract users." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use the right variant to communicate meaning and hierarchy." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don’t use a button variant for an action it’s not intended for." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Write button text that is clear, starts with a verb, and helps users confidently trigger an action." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Don’t use product or brand icons in buttons since they don’t communicate action." preview={false} mdxType="Dont" />
        </DoDont>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/button - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Button} from '@twilio-paste/button';

const Component = () => (
  <Button variant="secondary" size="small" onClick={() => {}}>
    Submit
  </Button>
);
`}</code></pre>
        <h4>{`Props`}</h4>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`button`}</inlineCode>{` are supported including the following props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`as?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The HTML tag to replace the default `}<inlineCode parentName="td">{`<button>`}</inlineCode>{` tag.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'button'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`disabled?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Prevent actions from firing on this button`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`fullWidth`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the button width to 100% of the parent container.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`href?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A URL to route to. Must use `}<inlineCode parentName="td">{`as="a"`}</inlineCode>{` for this prop to work.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`loading?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Prevent actions and show a loading spinner`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`size?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ButtonSizes`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default', 'small', 'icon', 'icon_small', 'reset'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'default'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`type?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ButtonTypes`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'button', 'submit', 'reset'. If the button is inside a `}<inlineCode parentName="td">{`<form>`}</inlineCode>{`: use 'submit'. Otherwise use 'button' (default).`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'button'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ButtonVariants`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'primary', 'secondary', 'inverse', 'destructive', 'destructive_secondary', 'destructive_link', 'link', 'inverse_link', 'reset'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'primary'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onClick?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onMouseDown?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onMouseUp?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onMouseEnter?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onMouseLeave?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.FocusEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onBlur?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.FocusEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      